/**
 * DISCOM Generation — ComposedChart + stat chips + top10/bottom10 tables.
 * FR-D04 to FR-D06
 */
import React, { useMemo, useState } from 'react';
import {
  ComposedChart, Bar, Line, Area, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import KPICard from '../../components/shared/KPICard';
import SectionDivider from '../../components/shared/SectionDivider';
import {
  discomGenChart30d,
  discomGenChartMonthly,
  discomGenChartHourly,
  pgvclGeneration,
  pgvclTop10Plants,
  pgvclBottom10Plants,
} from '../../data/mockData';

type Tab = 'daily' | 'monthly' | 'hourly';

const tabCls = (active: boolean) =>
  `px-3 py-1.5 text-xs rounded-input border transition-colors ${
    active ? 'bg-navy text-white border-navy' : 'bg-white text-text-secondary border-border hover:border-navy hover:text-navy'
  }`;

function StatChip({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-white border border-border rounded-card px-4 py-2.5 flex-1 min-w-0">
      <p className="text-xs text-text-secondary font-semibold leading-tight">{label}</p>
      <p className="text-sm font-bold text-text-primary mt-0.5 truncate">{value}</p>
    </div>
  );
}

function variance(actual: number, expected: number) {
  return (((actual - expected) / expected) * 100).toFixed(1);
}

const CustomTooltip = ({
  active, payload, label,
}: {
  active?: boolean;
  payload?: { value: number; name: string }[];
  label?: string;
}) => {
  if (!active || !payload || !payload.length) return null;
  const actual   = payload.find(p => p.name === 'actual')?.value ?? 0;
  const expected = payload.find(p => p.name === 'expected')?.value ?? 0;
  const ghi      = payload.find(p => p.name === 'ghi')?.value ?? 0;
  const varPct   = variance(actual, expected);
  return (
    <div className="bg-white border border-border rounded-card shadow-lg px-3 py-2 text-xs space-y-1">
      <p className="font-semibold text-text-primary mb-1">{label}</p>
      <p className="text-blue-600">Actual: <span className="font-semibold">{actual.toLocaleString()} MWh</span></p>
      <p className="text-indigo-500">GHI-Expected: <span className="font-semibold">{expected.toLocaleString()} MWh</span></p>
      <p className={parseFloat(varPct) < 0 ? 'text-red-500' : 'text-green-600'}>
        Variance: <span className="font-semibold">{parseFloat(varPct) > 0 ? '+' : ''}{varPct}%</span>
      </p>
      <p className="text-amber-500">GHI: <span className="font-semibold">{ghi} W/m²</span></p>
    </div>
  );
};

const prCls = (pr: number) =>
  pr >= 75 ? 'text-green-600 font-semibold' :
  pr >= 65 ? 'text-amber-600 font-semibold' :
             'text-red-600 font-semibold';

export default function DiscomGeneration() {
  const [tab, setTab] = useState<Tab>('daily');

  const chartData =
    tab === 'daily'   ? discomGenChart30d :
    tab === 'monthly' ? discomGenChartMonthly :
    discomGenChartHourly;

  const unit = tab === 'monthly' ? 'MWh' : tab === 'hourly' ? 'MW' : 'MWh';

  const stats = useMemo(() => {
    const actuals = chartData.map(d => d.actual);
    const total   = actuals.reduce((s, v) => s + v, 0);
    const avg     = total / actuals.length;
    const maxIdx  = actuals.indexOf(Math.max(...actuals));
    const minIdx  = actuals.indexOf(Math.min(...actuals));
    const avgGHI  = Math.round(chartData.reduce((s, d) => s + d.ghi, 0) / chartData.length);

    const fmt = (v: number) =>
      tab === 'monthly' ? `${Math.round(v).toLocaleString()} MWh` :
      tab === 'hourly'  ? `${Math.round(v)} MW` :
      `${Math.round(v).toLocaleString()} MWh`;

    return {
      total:  fmt(total),
      avg:    fmt(avg),
      best:   `${chartData[maxIdx]?.date} · ${fmt(actuals[maxIdx])}`,
      worst:  `${chartData[minIdx]?.date} · ${fmt(actuals[minIdx])}`,
      avgGHI: `${avgGHI} W/m²`,
    };
  }, [chartData, tab]);

  const periodLabel =
    tab === 'daily'   ? 'Last 30 days' :
    tab === 'monthly' ? 'Last 12 months' :
    'Today · 30-min intervals';

  return (
    <div className="p-6 space-y-5">
      {/* ── 6 KPI cards ── */}
      <div className="flex gap-4">
        <KPICard title="Today's Generation"          value="1,824 MWh"   subtext="+2.8% vs yesterday" subtextColor="green" />
        <KPICard title="This Month"                  value="44,820 MWh"  subtext="Apr 2026" />
        <KPICard title="Lifetime Total"              value="182 GWh"     subtext="PGVCL cumulative" />
        <KPICard title="Peak Power Today"            value="96.2 MW"     subtext="At 12:00 PM" />
        <KPICard title="AVG SPECIFIC YIELD TODAY"    value={`${pgvclGeneration.avgSpecificYield} kWh/kWp`} subtext="PGVCL today" />
        <KPICard title="AVG PERFORMANCE RATIO TODAY" value="78.9%"       subtext="PGVCL — Good zone" subtextColor="green" />
      </div>

      {/* ── 5 stat chips ── */}
      <div className="flex gap-3">
        <StatChip label="Period total"  value={stats.total} />
        <StatChip label="Daily average" value={stats.avg} />
        <StatChip label="Best day"      value={stats.best} />
        <StatChip label="Worst day"     value={stats.worst} />
        <StatChip label="Avg GHI"       value={stats.avgGHI} />
      </div>

      {/* ── ComposedChart ── */}
      <div className="bg-white rounded-card border border-border shadow-card p-4">
        <div className="flex items-center justify-between mb-4">
          <SectionDivider title="Generation vs GHI-Expected — PGVCL" subtitle={periodLabel} />
          <div className="flex items-center gap-1.5">
            <button className={tabCls(tab === 'daily')}   onClick={() => setTab('daily')}>Daily</button>
            <button className={tabCls(tab === 'monthly')} onClick={() => setTab('monthly')}>Monthly</button>
            <button className={tabCls(tab === 'hourly')}  onClick={() => setTab('hourly')}>Today Hourly</button>
          </div>
        </div>

        <ResponsiveContainer width="100%" height={300}>
          <ComposedChart data={chartData} margin={{ top: 8, right: 48, left: 0, bottom: 4 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#EEEEEE" vertical={false} />
            <XAxis
              dataKey="date"
              tick={{ fontSize: tab === 'daily' ? 9 : 11, fill: '#999' }}
              axisLine={false}
              tickLine={false}
              interval={tab === 'daily' ? 4 : tab === 'hourly' ? 3 : 0}
            />
            <YAxis
              yAxisId="gen"
              tick={{ fontSize: 11, fill: '#999' }}
              axisLine={false}
              tickLine={false}
              label={{ value: unit, angle: -90, position: 'insideLeft', offset: 10, style: { fontSize: 11, fill: '#999' } }}
            />
            <YAxis
              yAxisId="ghi"
              orientation="right"
              tick={{ fontSize: 11, fill: '#D97706' }}
              axisLine={false}
              tickLine={false}
              label={{ value: 'W/m²', angle: 90, position: 'insideRight', offset: 10, style: { fontSize: 11, fill: '#D97706' } }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend
              iconType="rect"
              iconSize={10}
              wrapperStyle={{ fontSize: 11, paddingTop: 8 }}
              formatter={(value: string) =>
                value === 'actual'   ? 'Actual Generation' :
                value === 'expected' ? 'GHI-Expected' :
                'Solar Irradiance (GHI)'
              }
            />
            <Area
              yAxisId="ghi"
              dataKey="ghi"
              fill="#FEF3C7"
              stroke="#F59E0B"
              strokeWidth={0}
              fillOpacity={0.5}
              dot={false}
              activeDot={false}
            />
            <Bar
              yAxisId="gen"
              dataKey="actual"
              fill="#3B82F6"
              radius={[3, 3, 0, 0]}
              maxBarSize={tab === 'daily' ? 16 : 28}
            />
            <Line
              yAxisId="gen"
              dataKey="expected"
              stroke="#6366F1"
              strokeWidth={2}
              strokeDasharray="5 3"
              dot={false}
              activeDot={{ r: 4 }}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {/* ── Top 10 / Bottom 10 plant tables ── */}
      <div className="space-y-4">
        {/* Top 10 */}
        <div className="bg-white rounded-card border border-border shadow-card overflow-hidden">
          <div className="px-4 py-3 border-b border-border">
            <p className="text-xs font-semibold text-text-primary">Top 10 Plants — Today's Generation</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-bg-secondary border-b border-border">
                <tr>
                  <th className="text-left py-2 px-3 text-text-secondary font-semibold">Plant ID</th>
                  <th className="text-left py-2 px-3 text-text-secondary font-semibold">Consumer</th>
                  <th className="text-left py-2 px-3 text-text-secondary font-semibold">Circle</th>
                  <th className="text-right py-2 px-3 text-text-secondary font-semibold">kWp</th>
                  <th className="text-right py-2 px-3 text-text-secondary font-semibold">Gen today</th>
                  <th className="text-right py-2 px-3 text-text-secondary font-semibold">Sp. yield</th>
                  <th className="text-right py-2 px-3 text-text-secondary font-semibold">PR %</th>
                  <th className="text-right py-2 px-3 text-text-secondary font-semibold">Avail %</th>
                  <th className="text-right py-2 px-3 text-text-secondary font-semibold">CUF %</th>
                  <th className="text-left py-2 px-3 text-text-secondary font-semibold">Active / Offline</th>
                  <th className="text-right py-2 px-3 text-text-secondary font-semibold">Alarms</th>
                </tr>
              </thead>
              <tbody>
                {pgvclTop10Plants.map(p => (
                  <tr key={p.id} className="border-b border-border/50 hover:bg-bg-secondary transition-colors">
                    <td className="py-2 px-3 font-mono text-accent hover:underline cursor-pointer">{p.id}</td>
                    <td className="py-2 px-3 text-text-secondary max-w-0 truncate" style={{ maxWidth: 120 }}>{p.consumer}</td>
                    <td className="py-2 px-3 text-text-tertiary">{p.circle.replace(' Circle', '')}</td>
                    <td className="py-2 px-3 text-right text-text-secondary">{p.capacityKWp}</td>
                    <td className="py-2 px-3 text-right font-medium text-text-primary">{p.generationKWh.toLocaleString()} kWh</td>
                    <td className="py-2 px-3 text-right text-text-secondary">{p.specificYield.toFixed(2)}</td>
                    <td className={`py-2 px-3 text-right ${prCls(p.prPercent)}`}>{p.prPercent.toFixed(1)}%</td>
                    <td className="py-2 px-3 text-right text-text-secondary">{p.availPercent.toFixed(0)}%</td>
                    <td className="py-2 px-3 text-right text-text-secondary">{p.cufPercent.toFixed(1)}%</td>
                    <td className="py-2 px-3">
                      <span className={`text-xs font-medium ${p.status === 'active' ? 'text-green-600' : 'text-red-600'}`}>
                        {p.status === 'active' ? 'Generating' : 'Offline'}
                      </span>
                    </td>
                    <td className={`py-2 px-3 text-right ${p.alarms > 0 ? 'text-red-600 font-semibold' : 'text-text-tertiary'}`}>{p.alarms > 0 ? p.alarms : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Bottom 10 */}
        <div className="bg-white rounded-card border border-border shadow-card overflow-hidden">
          <div className="px-4 py-3 border-b border-border">
            <p className="text-xs font-semibold text-text-primary">Bottom 10 Plants — Today's Generation</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-bg-secondary border-b border-border">
                <tr>
                  <th className="text-left py-2 px-3 text-text-secondary font-semibold">Plant ID</th>
                  <th className="text-left py-2 px-3 text-text-secondary font-semibold">Consumer</th>
                  <th className="text-left py-2 px-3 text-text-secondary font-semibold">Circle</th>
                  <th className="text-right py-2 px-3 text-text-secondary font-semibold">kWp</th>
                  <th className="text-right py-2 px-3 text-text-secondary font-semibold">Gen today</th>
                  <th className="text-right py-2 px-3 text-text-secondary font-semibold">Sp. yield</th>
                  <th className="text-right py-2 px-3 text-text-secondary font-semibold">PR %</th>
                  <th className="text-right py-2 px-3 text-text-secondary font-semibold">Avail %</th>
                  <th className="text-right py-2 px-3 text-text-secondary font-semibold">CUF %</th>
                  <th className="text-left py-2 px-3 text-text-secondary font-semibold">Active / Offline</th>
                  <th className="text-right py-2 px-3 text-text-secondary font-semibold">Alarms</th>
                </tr>
              </thead>
              <tbody>
                {pgvclBottom10Plants.map(p => (
                  <tr key={p.id} className="border-b border-border/50 hover:bg-orange-50/30 transition-colors">
                    <td className="py-2 px-3 font-mono text-accent hover:underline cursor-pointer">{p.id}</td>
                    <td className="py-2 px-3 text-text-secondary max-w-0 truncate" style={{ maxWidth: 120 }}>{p.consumer}</td>
                    <td className="py-2 px-3 text-text-tertiary">{p.circle.replace(' Circle', '')}</td>
                    <td className="py-2 px-3 text-right text-text-secondary">{p.capacityKWp}</td>
                    <td className="py-2 px-3 text-right font-medium text-text-primary">{p.generationKWh} kWh</td>
                    <td className="py-2 px-3 text-right text-text-secondary">{p.specificYield.toFixed(2)}</td>
                    <td className={`py-2 px-3 text-right ${prCls(p.prPercent)}`}>{p.prPercent.toFixed(1)}%</td>
                    <td className="py-2 px-3 text-right text-text-secondary">{p.availPercent.toFixed(0)}%</td>
                    <td className="py-2 px-3 text-right text-text-secondary">{p.cufPercent.toFixed(1)}%</td>
                    <td className="py-2 px-3">
                      <span className={`text-xs font-medium ${p.status === 'active' ? 'text-green-600' : 'text-red-600'}`}>
                        {p.status === 'active' ? 'Generating' : 'Offline'}
                      </span>
                    </td>
                    <td className={`py-2 px-3 text-right ${p.alarms > 0 ? 'text-red-600 font-semibold' : 'text-text-tertiary'}`}>{p.alarms > 0 ? p.alarms : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
